// glbIntegrity — does this body of bytes contain a whole GLB?
//
// A GLB says how long it is: the 12-byte header is magic 'glTF' (u32 LE
// 0x46546c67), version, then TOTAL length in bytes 8..12 as a u32 LE. So unlike
// the /ogs assets — which need the `size` off their list row to be checkable —
// a GLB is self-describing and can be verified with no server metadata at all.
//
// Motivating incident (gass, 2026-08-13): a truncated download of three of
// myra's body clips was written into the animation cache with no integrity
// check. The cache is cache-first, so every later load replayed the same short
// bytes and GLTFLoader threw the same `RangeError: Invalid typed array length`
// on every reload — while the server copies were byte-intact. Nothing short of
// a manual Clear Cache could dislodge it.
//
// The rule is ONE-SIDED, exactly as in cacheFirst.js's verifyBytes: only a
// SHORT body fails. A body LONGER than declared is not a broken download — a
// truncation cannot add bytes — so it passes, which is the same call
// aamAssetCache had to make when the AAM's rows turned out to understate the
// files they serve. Here an over-long body would mean trailing garbage after a
// complete GLB, which GLTFLoader ignores and which is nothing like the failure
// this guard exists to catch.
//
// Bytes that are not a GLB pass through UNTOUCHED: the same cache carries face
// clips (ARKit JSON) and kimodo tracks payloads, and this must never be the
// thing that rejects them.

/** 'glTF' read as a little-endian u32 — the first 4 bytes of every GLB. */
export const GLB_MAGIC = 0x46546c67

/** The 12-byte header (magic + version + length) that every GLB starts with. */
export const GLB_HEADER_BYTES = 12

/** Normalize an ArrayBuffer / TypedArray / DataView to a DataView over its own bytes. */
function viewOf(source) {
  if (!source) return null
  if (source instanceof ArrayBuffer) return new DataView(source)
  // A TypedArray may be a window into a larger buffer — honour byteOffset, or a
  // view onto a pooled buffer reads the wrong bytes entirely.
  if (ArrayBuffer.isView(source)) return new DataView(source.buffer, source.byteOffset, source.byteLength)
  return null
}

/**
 * The total byte length a GLB's header declares, or null if these bytes are not
 * a GLB (wrong magic, or too short to carry the 4-byte magic at all) and so have
 * nothing to check. A GLB whose header is itself truncated returns
 * GLB_HEADER_BYTES — the minimum a real GLB can be.
 *
 * @param {ArrayBuffer|ArrayBufferView} source
 * @returns {number|null}
 */
export function declaredGlbLength(source) {
  const dv = viewOf(source)
  if (!dv || dv.byteLength < 4) return null
  if (dv.getUint32(0, true) !== GLB_MAGIC) return null
  // 'glTF' but not even a full header: we can't read the declared length, but a
  // GLB is at least its header, so that's the shortfall we can prove.
  if (dv.byteLength < GLB_HEADER_BYTES) return GLB_HEADER_BYTES
  return dv.getUint32(8, true)
}

/**
 * How many bytes are MISSING from a truncated GLB. 0 means these bytes are fine
 * to use and to cache: an intact GLB, a longer-than-declared one, or something
 * that isn't a GLB at all.
 *
 * @param {ArrayBuffer|ArrayBufferView} source
 * @returns {number} 0 when acceptable, else the positive shortfall in bytes
 */
export function glbShortfall(source) {
  const declared = declaredGlbLength(source)
  if (declared == null) return 0
  const actual = viewOf(source).byteLength
  return actual < declared ? declared - actual : 0
}

/**
 * Human-readable reason a GLB body is unusable, or null when it's acceptable.
 * Shared by the cache's read side and the download's write side so a poisoned
 * entry and a truncated download read the same way in the console.
 *
 * @param {ArrayBuffer|ArrayBufferView} source
 * @returns {string|null}
 */
export function glbTruncationReason(source) {
  const short = glbShortfall(source)
  if (!short) return null
  const actual = viewOf(source).byteLength
  return `truncated GLB: ${actual} of ${actual + short} bytes declared by its own header (short by ${short})`
}
